/**
 * Task actions shared by the task pocket's More menu and the wall's new
 * task cards. Each takes the task's own project, so a card from another
 * project (Everything) acts through that project.
 */
import { isNotFoundError } from "@/api/client";
import { deleteTask, updateTaskStatus, type TaskStatus } from "@/api/tasks";
import {
  openTaskInProject,
  type ProjectItemTarget,
} from "@/components/layout/cross-project-navigation";
import { translate } from "@/i18n";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";
import { storesNoticesMessages } from "@/i18n/messages/stores-notices";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import { useTasksStore } from "@/stores/tasks-store";

/**
 * The pocket's More details page: in place when the pocket shows the task,
 * otherwise once the task opens (docked) in its project.
 */
export async function showTaskMoreDetails(
  task: ProjectItemTarget,
): Promise<void> {
  if (useTaskPocketStore.getState().taskId !== task.id) {
    await openTaskInProject(task);
  }
  const pocket = useTaskPocketStore.getState();
  if (pocket.taskId === task.id) pocket.setPage("details");
}

/** Deletes the task (one the server no longer has counts as deleted);
 *  false (after an error toast) when that failed. */
export async function deleteProjectTask(
  projectId: string,
  taskId: string,
): Promise<boolean> {
  try {
    await deleteTask(projectId, taskId);
  } catch (error) {
    if (!isNotFoundError(error)) {
      useNotificationStore.getState().addSystemToast({
        id: "task-delete-error",
        title: translate(panelsTasksMessages, "deleteFailed"),
        level: "error",
      });
      return false;
    }
  }
  // Removing the task from the store also closes its pocket.
  useTasksStore.getState().removeTask(taskId);
  return true;
}

/**
 * Moves the task to `status`. The current project's tasks go through the
 * tasks store (live, snapped back on a rejected move); another project's
 * through its own endpoint, leaving the store alone.
 */
export async function changeProjectTaskStatus(
  projectId: string,
  taskId: string,
  status: TaskStatus,
): Promise<boolean> {
  if (useProjectStore.getState().currentProject?.id === projectId) {
    return useTasksStore.getState().changeTaskStatus(projectId, taskId, status);
  }
  try {
    await updateTaskStatus(projectId, taskId, status);
    return true;
  } catch (error) {
    useNotificationStore.getState().addSystemToast({
      id: `task-status-error-${taskId}`,
      title:
        error instanceof Error && error.message
          ? error.message
          : translate(storesNoticesMessages, "statusChangeFailed"),
      level: "error",
    });
    return false;
  }
}
